<template>
  <!-- 8px are sommes of default margin and padding of text input -->
  <div :class="{'required': selfContent.required, 'editable': !selfContent.readOnly, 'auto-grow': autoGrow}" style="width: calc(100% - 8px)">
    <label>{{selfContent.label}}</label>
    <textarea v-model="selfContent.value"
           ref="textarea"
           class="input"
           placeholder="..."
           autocomplete="on"
           @input="onInput"
           :class="{'bad_input': selfContent.error, 'missing_input': isMissing}"
           @keyup.enter="login"
           :readonly="selfContent.readOnly"/>
  </div>
</template>

<script>
 import { library } from '@fortawesome/fontawesome-svg-core'
 import { faLock } from "@fortawesome/free-solid-svg-icons";

 library.add(faLock);

  export default {
      name: 'CustomTextAreaField',
      // autoGrow : le champ s'agrandit avec son contenu, dans la limite de la place disponible (minHeight : hauteur minimale en px)
      props: {
          content: {},
          autoGrow: {type: Boolean, default: false},
          minHeight: {type: Number, default: 80}
      },
      data: function () {
          return {
              selfContent: this.content,
          };
      },
      computed: {
          // Champ obligatoire encore vide
          isMissing() {
              const value = this.selfContent.value;
              return !!this.selfContent.required && !this.selfContent.readOnly && (value === '' || value === undefined || value === null);
          }
      },
      watch: {
          // Valeur modifiée de l'extérieur (ouverture d'une fiche) : la hauteur suit
          'selfContent.value'() {
              if (this.autoGrow) this.$nextTick(() => this.resize());
          }
      },
      mounted(){
          if (!this.autoGrow) return;
          this.resize();
          // Le champ peut être dans une section repliée (invisible) puis affichée, ou changer de largeur : on recalcule alors la hauteur
          this.lastWidth = this.$refs.textarea.offsetWidth;
          this.resizeObserver = new ResizeObserver(() => {
              const width = this.$refs.textarea.offsetWidth;
              if (width !== this.lastWidth) {
                  this.lastWidth = width;
                  // Reporté à l'image suivante : changer la hauteur pendant la notification de l'observateur provoque l'erreur « ResizeObserver loop »
                  cancelAnimationFrame(this.resizeFrame);
                  this.resizeFrame = requestAnimationFrame(() => this.resize());
              }
          });
          this.resizeObserver.observe(this.$refs.textarea);
          window.addEventListener('resize', this.resize);
      },
      beforeUnmount() {
          cancelAnimationFrame(this.resizeFrame);
          if (this.resizeObserver) this.resizeObserver.disconnect();
          window.removeEventListener('resize', this.resize);
      },
      methods: {
          onInput() {
              this.selfContent.error = false;
              if (this.autoGrow) this.resize();
          },
          // Conteneur qui défile (le corps de la modale) : c'est lui qui limite la place disponible
          findScrollContainer(element) {
              for (let parent = element.parentElement; parent; parent = parent.parentElement) {
                  const overflowY = getComputedStyle(parent).overflowY;
                  if (overflowY === 'auto' || overflowY === 'scroll') return parent;
              }
              return null;
          },
          // Hauteur = celle du texte (au moins minHeight) ; si la modale n'a plus de place, le champ se limite à la place restante
          // et seulement alors une barre de défilement apparaît dans le champ
          resize() {
              const textarea = this.$refs.textarea;
              if (!textarea || !textarea.offsetParent) return;
              const borders = textarea.offsetHeight - textarea.clientHeight;
              textarea.style.overflowY = 'hidden';
              textarea.style.height = this.minHeight + 'px';
              let height = Math.max(this.minHeight, textarea.scrollHeight + borders);
              textarea.style.height = height + 'px';
              const container = this.findScrollContainer(textarea);
              if (container) {
                  const overflow = container.scrollHeight - container.clientHeight;
                  if (overflow > 0) {
                      height = Math.max(this.minHeight, height - overflow);
                      textarea.style.height = height + 'px';
                  }
              }
              textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight + 1 ? 'auto' : 'hidden';
          }
      }
  }

</script>

<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl';

  div label
    display block
    margin 8px 0 4px 2px
    color CTRL_TEXT_MUTED
    font-size 0.82em
    font-weight 600

  .icon
    top 30px
    color THEME_COLOR_SECONDARY

  textarea
    // border-box : mêmes cotes qu'avant (largeur +8px pour le conteneur, hauteur -22px)
    box-sizing border-box
    width calc(100% + 8px)
    height calc(100% - 28px)

  // Champ qui s'agrandit : la hauteur est posée par le script, plus de poignée de redimensionnement
  .auto-grow
    textarea
      height auto
      resize none !important
      overflow-y hidden

  // Base = champ en lecture seule
  .input
    outline none
    background-color CTRL_DISABLED_BG
    border 1px solid transparent
    border-radius CTRL_RADIUS
    color CTRL_TEXT_MUTED
    padding 12px CTRL_PADDING_X
    line-height 1.4
    resize none
    transition background-color CTRL_EASE, border-color CTRL_EASE, box-shadow CTRL_EASE

  .input::placeholder
    color CTRL_PLACEHOLDER

  .editable
    label
      color CTRL_LABEL
    textarea
      background-color CTRL_BG
      border-color CTRL_BORDER
      color CTRL_TEXT
      resize: vertical
    textarea:hover
      background-color CTRL_BG_HOVER
      border-color CTRL_BORDER_HOVER
    textarea.missing_input
      box-shadow 0 0 7px CTRL_MISSING_GLOW
    textarea:focus
      background-color CTRL_BG_FOCUS
      border-color CTRL_FOCUS
      box-shadow 0 0 0 3px CTRL_FOCUS_RING

  .bad_input
    border-color CTRL_ERROR !important
    background-color CTRL_ERROR_BG !important

  .bad_input:focus
    box-shadow 0 0 0 3px CTRL_ERROR_RING !important

  .required label::after
    content " *"
    color CTRL_ERROR
</style>
